import {
  call,
  delay,
  put,
  race,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import {
  CLOSE_DOCUMENT,
  OPEN_DOCUMENT_SUCCESS,
  SET_PAGE_DIMENSIONS_BATCH,
} from '../actions';
import { extractMupdfDims } from '@redact-components/DocumentEditorPage/mupdfRenderWorkerClient';
import { recordMilestone } from '@redact-components/DocumentEditorPage/mainThreadSpans';

/**
 * Fast dims-only pre-pass (VE-27261).
 *
 * The virtualized viewers (editor canvas, redacted-file tab, Side-by-Side) need
 * accurate per-page reserved heights *immediately*: until a page's real size is
 * known its spacer falls back to DEFAULT_ASPECT, so a far scroll or a jump lays
 * out from the wrong heights and then reflows as pages render — the current-page
 * indicator visibly drifts (e.g. 500 -> 387) and the render window thrashes
 * against moving geometry. Every page's size is needed, not just the visible
 * ones, because the heights above a page determine where it sits.
 *
 * The full pre-pass (pdfPrepass.ts) also publishes dimensions, but it carries
 * text extraction with it (~1.4 ms/page vs ~0.01 ms for a bounds read), so on a
 * long document its last pages' heights are seconds behind. This pass exists to
 * beat it, and only ever writes dimensions, so the two can't clobber each other.
 *
 * ONE MuPDF request covers the whole document (~17 ms for 1600 pages, measured),
 * so there is nothing to stream — it lands well inside the first render.
 *
 * This used to run its own pdf.js document (its own worker, its own full
 * download of the file) purely to read `getViewport({ scale: 1 })` per page, and
 * streamed the results in chunks because that was slow enough to need it. Both
 * concerns that kept it on pdf.js checked out fine on MuPDF: `getBounds()`
 * applies /Rotate itself (a /Rotate 90 letter page reports 792x612, exactly as
 * pdf.js does), and the page walk is ~3.5x faster before counting the download
 * and worker it no longer needs.
 *
 * Best-effort: any failure is swallowed (the full pre-pass and the render path
 * still backfill the same dimensions). Note the fallback is now WEAKER than it
 * was: both dims sources are MuPDF, so a file MuPDF cannot open (encrypted,
 * damaged) leaves every spacer on DEFAULT_ASPECT. The pdf.js `getViewport` path
 * this replaced was the one independent reader, and the two engines used to fail
 * separately. Accepted — a document MuPDF can't open won't render either way.
 */
/**
 * Retry a transient worker failure, mirroring the text pre-pass's own retry and
 * for the same reason its docblock gives: this fires during open-time churn,
 * when an editor remount can dispose the shared worker mid-request. Without it a
 * single dispose left the document with NO page dimensions at all — and since a
 * page cannot raster until its dimensions are known, that silently pushed
 * time-to-first-page out to whenever pdf.js finished parsing instead.
 *
 * A GENERATOR, not an async function, so redux-saga cancellation reaches it. As a
 * plain async loop the CLOSE_DOCUMENT race below stopped the saga but not the
 * retries: they kept sleeping and re-calling after the editor was gone, and since
 * every enqueue cancels the worker's pending release, a retry could resurrect a
 * worker for a CLOSED document — re-downloading the file and re-instantiating
 * ~10 MB of WASM, then leaving it resident. That undid the very fix the race was
 * added for. `delay` and `call` are both cancellable; `setTimeout` and a bare
 * promise are not.
 */
export function* fetchDimsWithRetry(pdfUrl: string) {
  const MAX_ATTEMPTS = 4;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    // Milestones per attempt: 'page dims published' landing seconds after the
    // worker's own reported service time means the delay is HERE (a retry, or a
    // rejection the queue stats don't sample) rather than in the worker, and
    // without these there is no way to tell which.
    recordMilestone(`dims attempt ${attempt} requested`);
    try {
      const result = yield* call(extractMupdfDims, pdfUrl);
      recordMilestone(`dims attempt ${attempt} resolved`);
      return result;
    } catch (err) {
      lastErr = err;
      recordMilestone(`dims attempt ${attempt} REJECTED`);
      if (attempt < MAX_ATTEMPTS) {
        yield* delay(150 * attempt);
      }
    }
  }
  throw lastErr;
}

export function* runPdfDimsPrepass(pdfUrl: string) {
  if (!pdfUrl) {
    return;
  }
  try {
    const { pages } = yield* call(fetchDimsWithRetry, pdfUrl);
    // A page MuPDF couldn't measure comes back 0x0; drop it rather than
    // publishing a zero height, so the consumer keeps its own fallback.
    const batch = pages
      .map((dims, i) => ({ page: i + 1, dims }))
      .filter(({ dims }) => dims.width > 0 && dims.height > 0);
    if (batch.length > 0) {
      // Unblocks page rastering: DocumentPage's render effect is gated on
      // nativeWidth > 0, which this is the first source of.
      recordMilestone('page dims published');
      yield* put(SET_PAGE_DIMENSIONS_BATCH({ pages: batch }));
    }
  } catch {
    // Non-fatal — see the docblock's best-effort note.
  }
}

/**
 * takeLatest so re-opening a document abandons the previous pass rather than
 * letting it write the old document's dimensions.
 *
 * It does NOT cancel the worker-side job: `extractMupdfDims` takes no
 * AbortSignal, so the walk still runs to completion at HIGH priority for a
 * document the user has already left, ahead of the new one's first page renders.
 * The old eventChannel implementation could stop mid-walk. At ~17 ms for 1600
 * pages this is not worth an abort path today, but it is a real property that
 * changed, so don't read "takeLatest" here as "the work stops".
 */
export function* pdfDimsPrepassSaga() {
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, function* ({ payload }) {
    // Raced against CLOSE_DOCUMENT for the same reason as the text pre-pass:
    // leaving the editor should not leave work queued on the shared worker.
    yield* race({
      run: call(runPdfDimsPrepass, payload.pdfUrl),
      closed: take(CLOSE_DOCUMENT),
    });
  });
}
